
import {computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import InputText from "primevue/inputtext";
import {DocumentClassSetting} from "@dex/squeeze-client-ts";
import Dropdown from "primevue/dropdown";
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {useI18n} from "vue-i18n";
import MultiSelect from "primevue/multiselect";
import Chip from "primevue/chip";

export default defineComponent({
	name: "DocumentClassAttributesForm",
	components: {
		InputText,
		Dropdown,
		MultiSelect,
		Chip,
	},
	props: {
		documentClassSetting: {
			type: Object as PropType<DocumentClassSetting>,
			required: true,
		},
		allDocumentClassSettings: {
			type: Array as PropType<DocumentClassSetting[]>,
			required: true,
		},
		disableSettingName: {
			type: Boolean,
			default: false,
		},
		showErrorMessage: {
			type: Boolean,
			default: true,
		},
	},
	emits: ["update"],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Internal Object with all Field-Values */
		const value = reactive<DocumentClassSetting>({value: '', settingName: '', schema: {}});

		/** Schema object */
		const schema = reactive<any>({});

		/** List of selected enums of multi select */
		const selectedEnums = ref<any>([]);

		/** Determines the required rules for validation */
		const rules = {
			value: { required },
			settingName: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		/** List of booleans */
		const listOfBooleans = computed(() => {
			return [
				{
					id: 'true',
					name: t('Squeeze.General.Yes'),
				},
				{
					id: 'false',
					name: t('Squeeze.General.No'),
				},
			]
		});

		/** Triggered on change of any field */
		const update = async () => {
			await nextTick(); // Otherwise the change of the dropdown will not be recognised fast enough
			emit("update", value, v$.value.$invalid);
		}

		/** Triggered on change of multi select field */
		const updateMultiSelect = () => {
			// set value with comma separated
			value.value = selectedEnums.value.join(', ');
			update();
		}

		/**
		 * Triggered when the tooltip is shown
		 * @param values
		 */
		const mapTooltipValues = (values: string[]) => {
			return values
				.map((value: string, index: number) => {
					return `${index + 1}: ${value}`;
				})
				.join("\n");
		}

		/**
		 * Triggered when clicked to remove a selected item in multi select
		 * @param propertyOption
		 */
		const onRemovePropertyOption = (propertyOption: string) => {
			const index = selectedEnums.value.findIndex((option: string) => option === propertyOption);
			if (index !== -1) {
				// remove selected option
				selectedEnums.value.splice(index, 1);
				updateMultiSelect();
			}
		}

		/** Triggered on change of settingName field */
		const onChangeSettingName = () => {
			schema.example = '';
			schema.enum = undefined;
			schema.default = undefined;
			value.schema = {};
			value.value = '';
			if (value.settingName) {
				// get schema from selected setting
				const selectedSetting = props.allDocumentClassSettings.find((dcSetting: DocumentClassSetting) => dcSetting.settingName === value.settingName);
				if (selectedSetting) {
					Object.assign(value.schema, selectedSetting.schema);
					Object.assign(schema, value.schema);
					if (selectedSetting.schema.default) {
						value.value = selectedSetting.schema.default; // set default value

						// check if selected setting is of type multiSelect to set default value
						if (selectedSetting.schema.type === 'multiSelect') {
							selectedEnums.value.push(selectedSetting.schema.default);
						}
					}
				}
			}
			update();
		}

		/** Return all schema enums for dropdown */
		const getSchemaEnums = (schemaEnum: string): string => {
			return schemaEnum;
		}

		/**
		 * Check integer input value
		 * @param event
		 */
		const checkIntegerValue = (event: KeyboardEvent) => {
			const regex = /[0-9]|\./;
			if (!regex.test(event.key) && event.keyCode !== 8) {
				event.preventDefault();
				if (value.value) {
					value.value = value.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1').replace(/^0[^.]/, '0');
				}
				return;
			}
		}

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.documentClassSetting, () => {
			Object.assign(value, props.documentClassSetting);

			// set schema
			if (value.schema) {
				Object.assign(schema, value.schema);
				if (schema.default && value.value === '') {
					// set default value
					value.value = schema.default;

					// check if selected setting is of type multiSelect to set default value
					if (schema.type === 'multiSelect') {
						selectedEnums.value.push(schema.default);
						value.value = selectedEnums.value.join(', ');
					}
				} else if (schema.type === 'multiSelect' && value.value) {
					selectedEnums.value = value.value.split(', ');
				}
			}

			emit("update", value, v$.value.$invalid);
		})

		onMounted(async () => {
			Object.assign(value, props.documentClassSetting);

			// set schema by editing
			if (value.schema) {
				Object.assign(schema,value.schema);
				if (schema.default && value.value === '') {
					// set default value
					value.value = schema.default;

					// check if selected setting is of type multiSelect to set default value
					if (value.schema.type === 'multiSelect') {
						selectedEnums.value.push(value.schema.default);
						value.value = selectedEnums.value.join(', ');
					}
				} else if (schema.type === 'multiSelect' && value.value) {
					selectedEnums.value = value.value.split(', ');
				}
			}

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entires
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		return {
			value,
			schema,
			selectedEnums,
			v$,
			listOfBooleans,
			update,
			updateMultiSelect,
			onRemovePropertyOption,
			mapTooltipValues,
			getSchemaEnums,
			onChangeSettingName,
			showDropdownOverlay,
			checkIntegerValue,
		};
	},
});
